var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useMemo, useState } from 'react';
import { cx, css as emotionCss } from 'emotion';
import { inputChange, useCitrusForm } from '@citrus/form';
import { Input } from '@citrus/input';
import { getAutomation, getDebug, useCitrusDebug, useFormInputId, useUpdateValidation, useUserManagedValidation, useValidationOnMount, } from '@citrus/runtime';
import { isNotNilOrEmpty, isNilOrEmpty, only } from '@pcty/utils';
import { FieldSet } from './Checkbox.styled';
import { CheckboxContext } from './private/CheckboxContext';
var VERSION = '7.3.1';
var CheckboxList = React.forwardRef(function (_a, ref) {
    var _b;
    var automationId = _a.automationId, children = _a.children, className = _a.className, _c = _a.classNameWrapper, classNameWrapper = _c === void 0 ? '' : _c, _d = _a.columns, columns = _d === void 0 ? undefined : _d, css = _a.css, disabled = _a.disabled, _e = _a.emptyLabel, emptyLabel = _e === void 0 ? false : _e, errorMessage = _a.errorMessage, footnote = _a.footnote, _f = _a.hideOptionalLabel, hideOptionalLabel = _f === void 0 ? false : _f, _g = _a.highlighter, highlighter = _g === void 0 ? 'fuzzy' : _g, id = _a.id, label = _a.label, labelAddOn = _a.labelAddOn, onChange = _a.onChange, _h = _a.required, required = _h === void 0 ? false : _h, _j = _a.selectedIndexes, selectedIndexes = _j === void 0 ? [] : _j, _k = _a.showRequiredLabel, showRequiredLabel = _k === void 0 ? false : _k, showValidationState = _a.showValidationState, tooltip = _a.tooltip, valid = _a.valid, validate = _a.validate, _l = _a.__TYPE, __TYPE = _l === void 0 ? 'CheckboxList' : _l, other = __rest(_a, ["automationId", "children", "className", "classNameWrapper", "columns", "css", "disabled", "emptyLabel", "errorMessage", "footnote", "hideOptionalLabel", "highlighter", "id", "label", "labelAddOn", "onChange", "required", "selectedIndexes", "showRequiredLabel", "showValidationState", "tooltip", "valid", "validate", "__TYPE"]);
    var debug = useCitrusDebug();
    var _m = useState({ valid: false }), _validation = _m[0], setValidation = _m[1];
    var _o = useCitrusForm(), form = _o.form, dispatch = _o.dispatch, customValidate = _o.validate, formDisabled = _o.disabled;
    var formInputId = useFormInputId(id);
    var userManagedValidation = useUserManagedValidation(valid, errorMessage, showValidationState, setValidation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var updateForm = useCallback(inputChange(dispatch, customValidate), [customValidate]);
    var getValidation = useCallback(function (value) {
        if (userManagedValidation && !validate)
            return { valid: valid !== null && valid !== void 0 ? valid : true, errorMessage: errorMessage };
        var validation = { valid: true };
        if (required === true || typeof required === 'string') {
            validation = validateRequired(value);
        }
        if (validate && validation.valid) {
            validation = validate(value, {
                id: id,
                label: label,
                required: typeof required === 'string' ? true : required,
            });
        }
        return validation;
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errorMessage, form, id, label, required, valid, validate]);
    var validateRequired = useCallback(function (value) {
        if (isNilOrEmpty(value)) {
            return {
                valid: false,
                errorMessage: typeof required === 'string' ? required : "".concat(label !== null && label !== void 0 ? label : 'This', " is required"),
            };
        }
        return { valid: true };
    }, [label, required]);
    useValidationOnMount(id, label, selectedIndexes, userManagedValidation, getValidation, setValidation, updateForm);
    useUpdateValidation(form, formInputId, valid, errorMessage, _validation, setValidation);
    var context = useMemo(function () { return ({
        columns: columns,
        disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled,
        groupId: id,
        highlighter: highlighter,
        onChange: function (event) {
            if (selectedIndexes.indexOf(event.index) >= 0) {
                var _selectedIndexes_1 = selectedIndexes.filter(function (x) { return x !== event.index; });
                var validation_1 = getValidation(_selectedIndexes_1);
                setValidation(only(validation_1, ['valid', 'errorMessage']));
                onChange === null || onChange === void 0 ? void 0 : onChange(__assign(__assign({}, event), { selectedIndexes: _selectedIndexes_1, validation: validation_1 }));
                if (!userManagedValidation) {
                    updateForm(formInputId, __assign(__assign({}, validation_1), { value: _selectedIndexes_1, id: id, label: label, dirty: true }));
                }
                return;
            }
            var _selectedIndexes = __spreadArray(__spreadArray([], selectedIndexes, true), [event.index], false).sort();
            var validation = getValidation(_selectedIndexes);
            setValidation(only(validation, ['valid', 'errorMessage']));
            onChange === null || onChange === void 0 ? void 0 : onChange(__assign(__assign({}, event), { selectedIndexes: _selectedIndexes, validation: validation }));
            if (!userManagedValidation) {
                updateForm(formInputId, __assign(__assign({}, validation), { value: _selectedIndexes, id: id, label: label, dirty: true }));
            }
        },
    }); }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, formDisabled, id, onChange, columns, highlighter, selectedIndexes]);
    var classes = cx((_b = {},
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b), className || '');
    return (React.createElement(Input, { automationId: automationId, className: classNameWrapper, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, emptyLabel: emptyLabel, errorMessage: _validation.errorMessage, footnote: footnote, hideOptionalLabel: hideOptionalLabel, id: id, ignoreWrappedClick: true, label: label, labelAddOn: labelAddOn, ref: ref, required: typeof required === 'string' ? true : required, showRequiredLabel: showRequiredLabel, showValidationState: showValidationState !== undefined ? showValidationState : form === null || form === void 0 ? void 0 : form.showValidationState, tooltip: tooltip, valid: _validation.valid },
        React.createElement(FieldSet, __assign({ className: classes }, other, getAutomation(automationId), getDebug(debug, __TYPE, VERSION)),
            React.createElement(CheckboxContext.Provider, { value: context }, renderChildren(children, id, selectedIndexes)))));
});
CheckboxList.defaultProps = {
    __TYPE: 'CheckboxList',
};
var renderChildren = function (children, id, selectedIndexes) {
    var clonedChildren = React.Children.map(children, function (child, index) {
        var _a;
        if (((_a = child === null || child === void 0 ? void 0 : child.props) === null || _a === void 0 ? void 0 : _a.__TYPE) !== 'Checkbox')
            return null;
        var cloned = React.cloneElement(child, {
            index: index,
            key: "".concat(id, "-").concat(index),
            selected: selectedIndexes.indexOf(index) >= 0,
            value: child.props.value,
        });
        return cloned;
    });
    return clonedChildren;
};
export default CheckboxList;
